import {
  IField,
  IFormButton,
  IFormDocument,
  ITextDataBanner,
  IValue,
} from 'app/services/api5-service/api.interface';
import { getButtonList } from 'components/form-button/form-button.utils';
import { IFormButtonUi } from 'components/form-button/model';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { isControlHaveBackground } from 'common/dynamic-form/utils/is-control-have-background';
import { DocHelper } from 'utils/dochelper';

import { BannerProps } from './model/banner-props.interface';

import { ISPFieldConfig, ISPFieldType, ISPFormState } from '../../model';
import {
  getTextDataContent,
  getTextDataContent$,
} from '../textdata/textdata.utils';

export interface BannerContent extends IValue {
  /** Buttons for displaying in banner */
  button?: IFormButton | IFormButton[];
}

/**
 * Get buttons for banner
 *
 * @param control
 * @param doc
 */
function getBannerButtons(
  control: ITextDataBanner,
  doc: IFormDocument,
): IFormButtonUi[] {
  const bannerData = doc[control.$name] as BannerContent;
  const buttonsMeta = DocHelper.getArrayWrapperValue(bannerData?.button);
  return getButtonList(doc, buttonsMeta, false);
}

/**
 * Get stream of buttons for banner
 *
 * @param control - control (subfield) metadata
 * @param state - dynamic form state
 */
function getBannerButtons$(
  control: ITextDataBanner,
  state: ISPFormState,
): Observable<IFormButtonUi[]> {
  return state.docSubject.pipe(map(doc => getBannerButtons(control, doc)));
}

/**
 * Is banner should be hidden
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
function isBannerHidden(
  control: ITextDataBanner,
  field: IField,
  state: ISPFormState,
): boolean {
  const content = getTextDataContent(control, field, state.docSubject.value);
  const buttons = getBannerButtons(control, state.docSubject.value);
  return (
    state.hiddenService.isHidden(control.$name, field.$name) ||
    (!content && !buttons.length)
  );
}

/**
 * Get stream of banner hidden state
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
function isBannerHidden$(
  control: ITextDataBanner,
  field: IField,
  state: ISPFormState,
): Observable<boolean> {
  return combineLatest([
    state.hiddenService.isHidden$(control.$name, field.$name),
    state.docSubject,
  ]).pipe(map(() => isBannerHidden(control, field, state)));
}

/**
 * Get props for banner field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getBannerProps(
  control: ITextDataBanner,
  field: IField,
  state: ISPFormState,
): BannerProps {
  return {
    originalField: field,
    originalControl: control,
    type: control.$banner,
    buttons$: getBannerButtons$(control, state),
    content$: getTextDataContent$(control, field, state),
    isHidden: isBannerHidden(control, field, state),
    isHidden$: isBannerHidden$(control, field, state),
    maxWidth: isControlHaveBackground(state.docSubject.value, control)
      ? '600px'
      : undefined,
  };
}

/**
 * Get config for banner field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getBannerConfig(
  control: ITextDataBanner,
  field: IField,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.Banner> {
  const props = getBannerProps(control, field, state);

  return {
    type: ISPFieldType.Banner,
    props,
  };
}
